import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Users, Layers, X, Check } from 'lucide-react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { whatsappAPI } from '../whatsappAPI';
import { useOrganizacao, SeletorCor, Chip } from '../organizacao';
import { formatWa } from '../waFormat';

const VAZIO = { nome: '', cor: '#059669', membros: [], no_menu: true, ordem: 0, ativo: true };
const errMsg = (e, p) => e?.response?.data?.detail || p;

export default function SetoresConfig({ admin }) {
  const { setores, recarregar } = useOrganizacao();
  const [usuarios, setUsuarios] = useState([]);
  const [edit, setEdit] = useState(null);       // null | {…setor} (id ausente = novo)
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { whatsappAPI.atendentes().then(r => setUsuarios(r.data || [])).catch(() => {}); }, []);
  const nomeUser = (id) => usuarios.find(u => u.id === id)?.nome;

  const salvar = async () => {
    setSalvando(true);
    try {
      if (edit.id) await whatsappAPI.setorSalvar(edit.id, edit); else await whatsappAPI.setorCriar(edit);
      toast.success(edit.id ? 'Setor atualizado' : 'Setor criado');
      setEdit(null); await recarregar();
    } catch (e) { toast.error(errMsg(e, 'Não foi possível salvar')); }
    finally { setSalvando(false); }
  };
  const excluir = async (s) => {
    if (!window.confirm(`Excluir o setor "${s.nome}"?\nAs conversas dele ficam sem setor (fila geral) e as respostas automáticas que encaminhavam para ele são desativadas.`)) return;
    try { await whatsappAPI.setorExcluir(s.id); toast.success('Setor excluído'); recarregar(); }
    catch (e) { toast.error(errMsg(e, 'Não foi possível excluir')); }
  };

  const noMenu = setores.filter(s => s.ativo !== false && s.no_menu !== false);
  const previa = noMenu.length >= 2
    ? 'Com qual setor você quer falar? Responda com o *número*:\n\n' + noMenu.map((s, i) => `*${i + 1}* – ${s.nome}`).join('\n') + '\n\n*0* – Voltar ao menu'
    : noMenu.length === 1 ? `(vai direto para a fila de *${noMenu[0].nome}*)` : '(vai para a fila geral)';

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-4 items-start" data-testid="wa-config-setores">
      <Card className="p-5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h3 className="font-semibold flex items-center gap-2"><Layers className="w-5 h-5 text-emerald-600" /> Setores</h3>
          {admin && !edit && (
            <Button size="sm" onClick={() => setEdit({ ...VAZIO, ordem: setores.length + 1 })} data-testid="wa-setor-novo">
              <Plus className="w-4 h-4 mr-1" /> Novo setor
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Cada setor tem a sua fila. O cliente escolhe o setor no menu do assistente, é encaminhado por palavra-chave
          ou transferido por um atendente. Membros são as pessoas que atendem aquele setor.
        </p>

        {edit && (
          <div className="rounded-xl border-2 border-emerald-200 dark:border-emerald-900 bg-emerald-50/40 dark:bg-emerald-950/20 p-4 mb-4 space-y-3" data-testid="wa-setor-form">
            <div className="grid sm:grid-cols-[1fr_110px] gap-3">
              <label className="text-sm font-medium">Nome
                <Input value={edit.nome} onChange={e => setEdit({ ...edit, nome: e.target.value })} placeholder="Ex.: Financeiro" className="mt-1" autoFocus data-testid="wa-setor-nome" />
              </label>
              <label className="text-sm font-medium">Ordem no menu
                <Input type="number" min={0} value={edit.ordem} onChange={e => setEdit({ ...edit, ordem: Number(e.target.value) })} className="mt-1" />
              </label>
            </div>
            <div>
              <p className="text-sm font-medium mb-1.5">Cor</p>
              <SeletorCor value={edit.cor} onChange={cor => setEdit({ ...edit, cor })} />
            </div>
            <div>
              <p className="text-sm font-medium mb-1.5">Membros <span className="text-xs font-normal text-muted-foreground">(quem atende este setor)</span></p>
              <div className="flex flex-wrap gap-1.5">
                {usuarios.map(u => {
                  const on = edit.membros.includes(u.id);
                  return (
                    <button key={u.id} type="button" data-testid={`wa-setor-membro-${u.id}`}
                      onClick={() => setEdit({ ...edit, membros: on ? edit.membros.filter(x => x !== u.id) : [...edit.membros, u.id] })}
                      className={`h-8 px-3 rounded-full text-sm border flex items-center gap-1.5 transition-colors ${on ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                      {on && <Check className="w-3.5 h-3.5" />}{u.nome}
                    </button>
                  );
                })}
                {!usuarios.length && <span className="text-xs text-muted-foreground">Carregando usuários…</span>}
              </div>
            </div>
            <div className="flex flex-col gap-2 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={edit.no_menu} onChange={e => setEdit({ ...edit, no_menu: e.target.checked })} className="w-4 h-4 accent-emerald-600" />
                Aparece no menu do assistente (o cliente pode escolher)
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={edit.ativo} onChange={e => setEdit({ ...edit, ativo: e.target.checked })} className="w-4 h-4 accent-emerald-600" />
                Ativo
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEdit(null)}><X className="w-4 h-4 mr-1" />Cancelar</Button>
              <Button size="sm" onClick={salvar} disabled={salvando || !edit.nome.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-setor-salvar">
                {salvando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Salvar
              </Button>
            </div>
          </div>
        )}

        <div className="grid gap-2">
          {setores.map(s => (
            <div key={s.id} className={`flex items-center gap-3 p-3 rounded-xl border ${s.ativo === false ? 'opacity-60' : ''}`} data-testid={`wa-setor-${s.id}`}>
              <span className="w-10 h-10 rounded-xl shrink-0 flex items-center justify-center text-white font-semibold" style={{ background: s.cor }}>
                {s.nome.slice(0, 1).toUpperCase()}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium flex items-center gap-2 flex-wrap">
                  {s.nome}
                  {s.no_menu === false && <span className="text-[10.5px] px-1.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">fora do menu</span>}
                  {s.ativo === false && <span className="text-[10.5px] px-1.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">inativo</span>}
                </p>
                <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                  <Users className="w-3.5 h-3.5 shrink-0" />
                  {(s.membros || []).length ? (s.membros || []).map(nomeUser).filter(Boolean).join(', ') : 'Sem membros — qualquer atendente pode assumir'}
                </p>
              </div>
              {admin && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => setEdit({ ...VAZIO, ...s })} title="Editar"><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => excluir(s)} title="Excluir"><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </>
              )}
            </div>
          ))}
          {!setores.length && !edit && (
            <div className="text-center py-8 text-sm text-muted-foreground">
              Nenhum setor ainda. Sem setores, tudo vai para uma fila única (como hoje).
            </div>
          )}
        </div>
      </Card>

      <Card className="p-4 lg:sticky lg:top-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Cliente escolhe “2 – Falar com um atendente”</p>
        <div className="rounded-xl p-3 space-y-2" style={{ background: '#efeae2' }}>
          <div className="bg-white rounded-lg px-3 py-2 text-[13px] w-fit text-slate-900">2</div>
          <div className="bg-[#d9fdd3] rounded-lg px-3 py-2 text-[13px] whitespace-pre-wrap ml-auto max-w-[92%] text-slate-900">{formatWa(previa)}</div>
        </div>
        <div className="flex flex-wrap gap-1 mt-3">
          {noMenu.map(s => <Chip key={s.id} cor={s.cor}>{s.nome}</Chip>)}
        </div>
      </Card>
    </div>
  );
}
